import React, { useMemo } from "react";
import { Col, Form, FormControl, FormGroup, FormLabel, Row } from "react-bootstrap";
import { Controller, UseFormReturn } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";
import { Checkbox } from "../../shared/widgets/checkbox";
import { DisplayEntity, DisplayFormType } from "../types";
import { ErrorFieldMessage } from "../../extras/components/ErrorFieldMessage";
import { InputToggle } from "../../extras/components/InputToggle";
import { messages } from "../messages";
import { WitLabel } from "../../extras/EditExtra/styles";
import { OpenBooking } from "../components/OpenBooking";
import styled from "styled-components";

export enum CRUD_MODE {
  ADD = "ADD",
  EDIT = "EDIT",
  DUPLICATE = "DUPLICATE",
}

interface PropertiesPanelProps {
  display: DisplayEntity | null;
  methods: UseFormReturn<DisplayFormType>;
  mode?: CRUD_MODE;
}

const TICKER_MAX_LENGTH = 20;

const BigWitLabel = styled(WitLabel)``;

export const PropertiesPanel: React.FC<PropertiesPanelProps> = ({ methods, mode }) => {
  const intl = useIntl();
  const { register, control, watch } = methods;

  const entityFilter = watch("entityFilter");
  const isModification = useMemo(() => CRUD_MODE.EDIT === mode, [mode]);

  return (
    <div style={{ padding: "0 26px 0 26px" }}>
      {isModification && (
        <div className="text-muted" style={{ textAlign: "left", marginBottom: 8 }}>
          <em>
            <FormattedMessage id="display.editing" defaultMessage="Editando display existente" />
          </em>
        </div>
      )}

      <Row className="mb-0">
        <Col className="pl-3">
          <BigWitLabel
            className="mb-2"
            style={{
              lineHeight: 1.2,
              display: "block",
              borderBottom: "1px solid rgba(0,0,0,.12)",
              paddingBottom: 6,
              marginTop: 2,
              marginBottom: 10,
            }}>
            <FormattedMessage {...messages.active} />
          </BigWitLabel>
        </Col>
      </Row>

      <Row>
        <Col className="pl-3">
          <Controller
            name="active"
            control={control}
            render={({ field }) => (
              <InputToggle className="witadmin-bs3" {...field} value={!!field.value} />
            )}
          />
          <Form.Text className="text-muted mb-3" style={{ marginTop: 4 }}>
            <FormattedMessage {...messages.activeHelp} />
          </Form.Text>
        </Col>
      </Row>

      {/* Open by default */}
      <Row className="mt-3">
        <Col className="pl-3">
          <Form.Label className="mb-0">
            <FormattedMessage {...messages.defaultOption} />
          </Form.Label>
          {/* Checkbox */}
          <div style={{ marginTop: 0 }}>
            <Controller
              name="defaultOption"
              control={control}
              render={({ field }) => (
                <Checkbox
                  id="defaultOption"
                  required={false}
                  disabled={false}
                  readonly={false}
                  autofocus={false}
                  label=""
                  options={{}}
                  formContext={{}}
                  rawErrors={[]}
                  schema={{ title: "" } as any}
                  value={!!field.value}
                  onChange={(v: boolean) => field.onChange(v)}
                  onBlur={() => {}}
                  onFocus={() => {}}
                />
              )}
            />
            <Form.Text className="text-muted mt-1 mb-4">
              <FormattedMessage {...messages.defaultOptionHelp} />
            </Form.Text>
          </div>
        </Col>
      </Row>

      {/* Id */}
      <Row className="mb-2">
        <Col sm={2}>
          <FormGroup className="mb-0">
            <FormLabel className="mb-2">
              <FormattedMessage {...messages.displayTicker} /> *
            </FormLabel>
            <Controller
              name="ticker"
              control={control}
              render={({ field }) => (
                <FormControl
                  readOnly={CRUD_MODE.EDIT === mode}
                  {...field}
                  maxLength={TICKER_MAX_LENGTH}
                />
              )}
            />
            {methods.formState.errors.ticker?.type === "required" && (
              <ErrorFieldMessage {...messages.presenceValidator} />
            )}
          </FormGroup>
        </Col>
      </Row>

      {/* Dates */}

      <FormGroup as={Row} className="mb-2">
        <FormLabel column sm={12}>
          <WitLabel>
            <FormattedMessage id="display.period" defaultMessage="Período de visualización" />
          </WitLabel>
        </FormLabel>

        <Col sm={3}>
          <OpenBooking
            key={`${watch("startDate")}-${watch("endDate")}`}
            disabled={false}
            field={{ isRange: false, startField: "startDate", endField: "endDate" }}
            defaultValues={{
              startDate: watch("startDate") || null,
              endDate: watch("endDate") || null,
            }}
            onChange={(data: { startDate?: string | null; endDate?: string | null }) => {
              if (data?.startDate !== undefined)
                methods.setValue("startDate", data.startDate ?? "", { shouldDirty: true });
              if (data?.endDate !== undefined)
                methods.setValue("endDate", data.endDate ?? "", { shouldDirty: true });
            }}
            intl={intl}
          />
        </Col>
      </FormGroup>

      {/* Display scope */}
      <Row className="mb-4">
        <FormLabel column sm={12}>
          <WitLabel>
            <FormattedMessage id="display.type" defaultMessage="Tipo" />
          </WitLabel>
        </FormLabel>
        <Col md={2}>
          <Form.Group>
            <Form.Label>
              <FormattedMessage {...messages.scope} />
            </Form.Label>
            <Form.Control as="select" {...register("entityFilter")}>
              <option value="PROPERTY">
                <FormattedMessage {...messages.locationProperty} />
              </option>
              <option value="ACCOMMODATION">
                <FormattedMessage {...messages.locationAccommodation} />
              </option>
            </Form.Control>
          </Form.Group>
        </Col>

        {entityFilter === "ACCOMMODATION" && (
          <Col md={4}>
            <Form.Group>
              <Form.Label>
                <FormattedMessage {...messages.tickerFilter} />
              </Form.Label>
              <Form.Control {...register("tickerFilter")} placeholder="ABC123, ROOM01" />
            </Form.Group>
          </Col>
        )}
      </Row>

      {/* Content Type */}
      <Row className="mb-1">
        <Col md={2}>
          <Form.Group>
            <Form.Label>
              <FormattedMessage {...messages.contentType} />
            </Form.Label>
            <Form.Control as="select" {...register("contentType")}>
              <option value="HTML">
                <FormattedMessage {...messages.html} />
              </option>
              <option value="VIDEO">
                <FormattedMessage {...messages.video} />
              </option>
              <option value="IMAGE">
                <FormattedMessage {...messages.image} />
              </option>
            </Form.Control>
          </Form.Group>
        </Col>
      </Row>
    </div>
  );
};
